// The site's tag pages: which tags and values have one, and what each says
// (its title, text, tab and badge). The pages themselves are list-pages.ts's.
// Nothing here touches the DOM.

import type { TagValue } from "../../../shared/api.ts";
import { AROUND_THE_CLOCK, EVENINGS } from "../../../shared/evenings.ts";
import { numberOrNull } from "../../../shared/guards.ts";
import {
  CHANGE_MIN_PLAYERS,
  MOVERS_PER_TAG,
  NEAR_PEAK_SHARE,
  NEW_RELEASE_DAYS,
  NEW_RELEASE_GAMES,
  PEAK_MIN_DAYS,
  SALE_BUMP_DAYS,
  SALE_BUMP_GAMES,
  SALE_BUMP_MIN,
  SALE_MIN_DISCOUNT,
  UPDATE_BUMP_DAYS,
  UPDATE_BUMP_GAMES,
  UPDATE_BUMP_MIN
} from "../../../shared/limits.ts";
import { TAG_PAGE_VALUES } from "../../../shared/site.ts";
import { HOUR_SECONDS, PRICES_MAX_AGE } from "../../../shared/time.ts";
import { percentOff } from "../store-facts.ts";
import type { Way } from "../web.ts";

// A tag page's value: see TAG_PAGES.
export interface TagPageValue {
  title: string;
  tab: string;
  text: string;
  short: string;
  badge?: {
    // Its text, or one made from the game's tag value ({ state, value, ... }).
    text: string | ((value: TagValue) => string);
    way: Way;
    about: string;
    // False to leave it off game pages, which say it some other way.
    onGamePage?: false;
  };
  // What its page says with no games, if not the usual.
  empty?: string;
}

// A tag with pages: its name, the filter its values are read by, and its
// values.
interface TagPageTag {
  name: string;
  param: string;
  values: Record<string, TagPageValue>;
}

const minPlayers = CHANGE_MIN_PLAYERS.toLocaleString("en-US");
const nearPercent = Math.round(NEAR_PEAK_SHARE * 100);
const bumpPercent = Math.round(UPDATE_BUMP_MIN * 100);
const saleBumpPercent = Math.round(SALE_BUMP_MIN * 100);

// The site's tag pages, one per tag and state (or zone): /tags/<tag>/<value>
// lists the ranked games with it, like the home page. The tags are
// workers/shared/tags.ts's; `param` is the GET /api/tags/<tag> filter a value
// is read by. Each value has its page's `title` and `text`, and `tab`, its
// short name in the tabs between a tag's pages. A state of a tag with states
// may have a `badge`, { text, way (a WAYS key), about: what it means }, shown
// on game pages and list rows, which carry every such tag; each goes on the
// ranked games showing it most (MOVERS_PER_TAG of them for a movers tag), as
// of the tag's latest run. A value also has `short`, a line saying what its
// games have in common, for its card on page 0. A tag or value not listed
// here (one added since) has no page and shows nothing; build.ts checks that
// every one listed is a tag, filter and state the API knows.
export const TAG_PAGES = {
  trend: {
    name: "24h change",
    param: "state",
    values: {
      rising: {
        title: "Top risers",
        tab: "Risers",
        text: `The ${MOVERS_PER_TAG} games whose players rose most in the last 24 hours, among games with at least ${minPlayers} players a day earlier.`,
        short: "Games whose players rose most in 24h.",
        badge: { text: "Top riser · 24h", way: "up", about: `One of the ${MOVERS_PER_TAG} games whose players rose most in 24h` }
      },
      falling: {
        title: "Top fallers",
        tab: "Fallers",
        text: `The ${MOVERS_PER_TAG} games whose players fell most in the last 24 hours, among games with at least ${minPlayers} players a day earlier.`,
        short: "Games whose players fell most in 24h.",
        badge: { text: "Top faller · 24h", way: "down", about: `One of the ${MOVERS_PER_TAG} games whose players fell most in 24h` }
      }
    }
  },
  rank: {
    name: "Rank change",
    param: "state",
    values: {
      climbing: {
        title: "Top climbers",
        tab: "Climbers",
        text: `The ${MOVERS_PER_TAG} games that climbed the most places in the ranking in the last 24 hours.`,
        short: "Games that climbed the most places in 24h.",
        badge: { text: "Top climber · 24h", way: "up", about: `One of the ${MOVERS_PER_TAG} games that climbed the most places in 24h` }
      },
      dropping: {
        title: "Biggest drops",
        tab: "Drops",
        text: `The ${MOVERS_PER_TAG} games that dropped the most places in the ranking in the last 24 hours.`,
        short: "Games that dropped the most places in 24h.",
        badge: { text: "Top drop · 24h", way: "down", about: `One of the ${MOVERS_PER_TAG} games that dropped the most places in 24h` }
      }
    }
  },
  peak_share: {
    name: "Against the all-time peak",
    param: "state",
    values: {
      near: {
        title: "Near their peak",
        tab: "Near peak",
        text: `The ${MOVERS_PER_TAG} games closest to their all-time peak, each at ${nearPercent}% of it or more, among games tracked for at least ${PEAK_MIN_DAYS} days.`,
        short: `Games within ${100 - nearPercent}% of their all-time peak.`,
        badge: { text: "Near its peak", way: "up", about: `Within ${100 - nearPercent}% of its all-time peak: one of the ${MOVERS_PER_TAG} games closest to theirs` }
      },
      far: {
        title: "Far below their peak",
        tab: "Far below peak",
        text: `The ${MOVERS_PER_TAG} games furthest below their all-time peak, among games tracked for at least ${PEAK_MIN_DAYS} days with a peak of at least ${minPlayers} players.`,
        short: "Games furthest below their all-time peak.",
        badge: { text: "Far below its peak", way: "down", about: `One of the ${MOVERS_PER_TAG} games furthest below their all-time peak` }
      }
    }
  },
  update_bump: {
    name: "After an update",
    param: "state",
    values: {
      up: {
        title: "Bumped by an update",
        tab: "Update bump",
        text: `The ${UPDATE_BUMP_GAMES} games whose players rose most after a major or regular update posted in the last ${UPDATE_BUMP_DAYS} days: the average over the week after it against the week before, or, until a week is in, its days so far against the same weekdays. Among games with at least ${minPlayers} players the week before, each up ${bumpPercent}% or more.`,
        short: "Games whose players rose most after a recent update.",
        badge: { text: "Update bump", way: "up", about: `One of the ${UPDATE_BUMP_GAMES} games whose players rose most in the week after a recent major or regular update` }
      }
    }
  },
  sale_bump: {
    name: "During a sale",
    param: "state",
    values: {
      up: {
        title: "Bumped by a sale",
        tab: "Sale bump",
        text: `The ${SALE_BUMP_GAMES} games whose players rose most during a sale reaching ${SALE_MIN_DISCOUNT}% off or more that started in the last ${SALE_BUMP_DAYS} days, on the US store: the average over its days, up to a week, against the week before it, or, until a week is in, its days so far against the same weekdays. A sale that ended counts only the days it ran. Among games at full price, with no sale in that week, and at least ${minPlayers} players the week before, each up ${saleBumpPercent}% or more.`,
        short: "Games whose players rose most during a recent sale.",
        empty: `No games right now. Prices have been tracked since November 2026, and a sale only counts with a week at full price before it.`,
        badge: { text: "Sale bump", way: "up", about: `One of the ${SALE_BUMP_GAMES} games whose players rose most during a recent sale` }
      }
    }
  },
  on_sale: {
    name: "On sale",
    param: "state",
    values: {
      now: {
        title: "On sale now",
        tab: "On sale",
        text: `The tracked games on sale on the US store at ${SALE_MIN_DISCOUNT}% off or more, the biggest discount first, then the most played. Prices are checked every ${PRICES_MAX_AGE / HOUR_SECONDS} hours.`,
        short: `Games on sale now, at ${SALE_MIN_DISCOUNT}% off or more.`,
        empty: "No tracked games are on sale right now. Prices have been tracked since November 2026.",
        badge: {
          text: (value) => {
            const discount = numberOrNull(value.value);
            return discount == null ? "On sale" : percentOff(discount);
          },
          way: "flat",
          about: `On sale now on the US store, at ${SALE_MIN_DISCOUNT}% off or more`,
          // The price in the page's head already says a sale, with its badge.
          onGamePage: false
        }
      }
    }
  },
  new_release: {
    name: "New releases",
    param: "state",
    values: {
      recent: {
        title: "New releases",
        tab: "New releases",
        text: `The most played games released in the last ${NEW_RELEASE_DAYS} days, up to ${NEW_RELEASE_GAMES} of them, by the release date on their Steam store page. A game that left early access dates from its full release.`,
        short: `The most played games released in the last ${NEW_RELEASE_DAYS} days.`,
        badge: { text: "New release", way: "flat", about: `Released in the last ${NEW_RELEASE_DAYS} days, and among the ${NEW_RELEASE_GAMES} most played games out that recently` }
      }
    }
  },
  busy_hours: {
    name: "When it's played",
    param: "zone",
    values: {
      ...Object.fromEntries(
        Object.entries(EVENINGS).map(([region, { label, place, from }]): [string, TagPageValue] => [
          region,
          {
            title: `Played in ${label} evenings`,
            tab: label,
            text: `Games busiest in the evening in ${place} (from ${from}:00 local time) over the last four weeks, the most evening-heavy first.`,
            short: `Games busiest in the evening in ${place}.`
          }
        ])
      ),
      [AROUND_THE_CLOCK]: {
        title: "Played around the clock",
        tab: "Around the clock",
        text: "Games with no evening peak over the last four weeks, about as busy at one time of day as another, the flattest first.",
        short: "Games with no evening peak, as busy at any hour."
      }
    }
  }
} satisfies { [T in keyof typeof TAG_PAGE_VALUES]: TagPageTag };

// A tag with pages: a key of TAG_PAGES.
export type TagPageName = keyof typeof TAG_PAGES;

export const isTagPageName = (name: string | undefined): name is TagPageName =>
  name !== undefined && Object.hasOwn(TAG_PAGES, name);

// A tag page's value of `tag`, by own key only, as the name may be a path's.
export const tagPageValue = (tag: TagPageName, value: string | undefined): TagPageValue | undefined => {
  const values: Record<string, TagPageValue> = TAG_PAGES[tag].values;
  return value !== undefined && Object.hasOwn(values, value) ? values[value] : undefined;
};

// A tag page: its tag, value and TAG_PAGES value.
export interface TagPageRef {
  tag: TagPageName;
  value: string;
  def: TagPageValue;
}

// Each tag with pages, in TAG_PAGES order.
export const TAG_PAGE_NAMES: readonly TagPageName[] = Object.keys(TAG_PAGES).filter(isTagPageName);
